/**
 * PKCE helpers (S256) using Web Crypto.
 */

/**
 * Generates a high-entropy PKCE `code_verifier` (43–128 chars, URL-safe).
 *
 * @returns Random code verifier string.
 */
export function generateCodeVerifier(): string {
    const bytes = new Uint8Array( 32 );
    crypto.getRandomValues( bytes );

    return base64UrlEncode( bytes );
}

/**
 * Generates a random OAuth `state` value.
 *
 * @returns Random state string.
 */
export function generateOAuthState(): string {
    const bytes = new Uint8Array( 16 );
    crypto.getRandomValues( bytes );

    return base64UrlEncode( bytes );
}

/**
 * Computes the S256 `code_challenge` for a given verifier.
 *
 * @param codeVerifier - PKCE code verifier.
 * @returns Base64url-encoded SHA-256 digest.
 */
export async function computeCodeChallengeS256( codeVerifier: string ): Promise<string> {
    const data = new TextEncoder().encode( codeVerifier );
    const digest = await crypto.subtle.digest( "SHA-256", data );

    return base64UrlEncode( new Uint8Array( digest ) );
}

/**
 * Encodes bytes as base64url without padding.
 *
 * @param bytes - Input bytes.
 * @returns Base64url string.
 */
function base64UrlEncode( bytes: Uint8Array ): string {
    let binary = "";
    for ( const byte of bytes ) {
        binary += String.fromCharCode( byte );
    }

    return btoa( binary ).replace( /\+/g, "-" ).
        replace( /\//g, "_" ).
        replace( /=+$/u, "" );
}
